import React, { useState, useEffect } from 'react';
import {
    ExclamationTriangleIcon,
    TrashIcon,
    XMarkIcon,
    CheckCircleIcon,
    ArrowRightIcon,
    DocumentTextIcon,
    ShieldExclamationIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';
import {
    collection,
    query,
    where,
    getDocs,
    deleteDoc,
    doc,
    updateDoc,
    addDoc,
    getDoc,
} from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import type { SrmProject, Organization } from '@types';
import { getOrganizationDisplayName } from '@common/organizationDisplayName';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DecommissionImpact {
    frameworkDocs: number;
    linkedReports: number;
    linkedReviews: number;
    linkedLibraryItems: number;
    riskCount: number;
    moduleCount: number;
}

interface DecommissionStep {
    label: string;
    status: 'pending' | 'running' | 'done' | 'error';
}

const DECOMMISSION_REASONS = [
    'Project / Engagement Complete',
    'Client Offboarded',
    'Scope Change — Framework Superseded',
    'Duplicate Framework',
    'Created in Error',
    'Organizational Restructure',
    'Other (see notes)',
] as const;

// ─── Modal Component ─────────────────────────────────────────────────────────

interface Props {
    isOpen: boolean;
    project: SrmProject;
    organization: Organization | null;
    onClose: () => void;
    onComplete: () => void;
}

export const FrameworkDecommissionModal: React.FC<Props> = ({
    isOpen,
    project,
    organization,
    onClose,
    onComplete,
}) => {
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const [impact, setImpact] = useState<DecommissionImpact | null>(null);
    const [loadingImpact, setLoadingImpact] = useState(false);
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [execSteps, setExecSteps] = useState<DecommissionStep[]>([]);
    const [decommissionLogId, setDecommissionLogId] = useState<string | null>(null);
    const [execError, setExecError] = useState<string | null>(null);

    const projectName = project?.name || project?.projectName || 'Untitled Framework';

    // ── Load impact counts when modal opens ──────────────────────────────────

    useEffect(() => {
        if (!isOpen || !project?.id || !db) return;
        setStep(1);
        setConfirmText('');
        setReason('');
        setNotes('');
        setImpact(null);
        setDecommissionLogId(null);
        setExecError(null);
        setLoadingImpact(true);

        const fetchImpact = async () => {
            try {
                const [frameworkSnap, reportsSnap, reviewsSnap, librarySnap] = await Promise.allSettled([
                    getDocs(query(collection(db, 'active_frameworks'), where('projectId', '==', project.id))),
                    getDocs(query(collection(db, 'generated_reports'), where('projectId', '==', project.id))),
                    getDocs(query(collection(db, 'plan_reviews'), where('projectId', '==', project.id))),
                    getDocs(query(collection(db, 'saved_intel'), where('mappedTo', '==', project.id))),
                ]);
                setImpact({
                    frameworkDocs: frameworkSnap.status === 'fulfilled' ? frameworkSnap.value.size : 0,
                    linkedReports: reportsSnap.status === 'fulfilled' ? reportsSnap.value.size : 0,
                    linkedReviews: reviewsSnap.status === 'fulfilled' ? reviewsSnap.value.size : 0,
                    linkedLibraryItems: librarySnap.status === 'fulfilled' ? librarySnap.value.size : 0,
                    riskCount: (project.risks || []).length,
                    moduleCount: (project.modules || []).length,
                });
            } catch {
                setImpact({ frameworkDocs: 0, linkedReports: 0, linkedReviews: 0, linkedLibraryItems: 0, riskCount: 0, moduleCount: 0 });
            } finally {
                setLoadingImpact(false);
            }
        };

        fetchImpact();
    }, [isOpen, project?.id]);

    // ── Execute decommission ──────────────────────────────────────────────────

    const executeDecommission = async () => {
        if (!db || !project?.id) return;
        setStep(4);
        setExecError(null);

        const steps: DecommissionStep[] = [
            { label: 'Recording decommission intent', status: 'pending' },
            { label: 'Unlinking active framework documents', status: 'pending' },
            { label: 'Removing linked library reports', status: 'pending' },
            { label: 'Removing linked plan reviews', status: 'pending' },
            { label: 'Removing linked generated reports', status: 'pending' },
            { label: 'Deleting SRM project record', status: 'pending' },
        ];
        setExecSteps([...steps]);

        const updateStep = (index: number, status: DecommissionStep['status']) => {
            setExecSteps(prev => prev.map((s, i) => i === index ? { ...s, status } : s));
        };

        const auditPayload = {
            projectId: project.id,
            projectName,
            organizationId: organization?.id || (project as any).organizationId || '',
            organizationName: getOrganizationDisplayName(organization),
            decommissionedBy: auth.currentUser?.email || auth.currentUser?.uid || 'System',
            decommissionedAt: new Date().toISOString(),
            reason,
            notes: notes || null,
            status: 'Decommissioned',
            projectSnapshot: {
                id: project.id,
                name: projectName,
                status: project.status,
                moduleCount: (project.modules || []).length,
                riskCount: (project.risks || []).length,
                createdAt: (project as any).createdAt || null,
            },
        };

        // Step 0 — Stamp decommission metadata on the project document itself (FATAL).
        // org members already have updateDoc permission on srm_projects.
        // A Cloud Function onDelete trigger will capture this and write to decommission_log.
        try {
            updateStep(0, 'running');
            await updateDoc(doc(db, 'srm_projects', project.id), {
                _decommissionRecord: auditPayload,
                status: 'Decommissioning',
                _decommissioningAt: new Date().toISOString(),
            });
            // Also attempt client-side decommission_log write (non-fatal — Cloud Function is the backup)
            try {
                const logRef = await addDoc(collection(db, 'decommission_log'), auditPayload);
                setDecommissionLogId(logRef.id);
            } catch (logErr) {
                console.warn('[Decommission] decommission_log client write blocked (Cloud Function will handle):', logErr);
            }
            updateStep(0, 'done');
        } catch (err: any) {
            console.error('[Decommission] Step 0 FATAL (project update):', err);
            setExecError(`Failed to record decommission intent: ${err?.message}`);
            updateStep(0, 'error');
            return;
        }

        // Step 1 — Active frameworks (non-fatal)
        updateStep(1, 'running');
        try {
            const [fwByProject, fwByName] = await Promise.allSettled([
                getDocs(query(collection(db, 'active_frameworks'), where('projectId', '==', project.id))),
                getDocs(query(collection(db, 'active_frameworks'), where('name', '==', `${projectName} Framework`))),
            ]);
            const toDeleteFw = new Set<string>();
            if (fwByProject.status === 'fulfilled') fwByProject.value.docs.forEach(d => toDeleteFw.add(d.id));
            if (fwByName.status === 'fulfilled') fwByName.value.docs.forEach(d => {
                if (d.data().organizationId === organization?.id) toDeleteFw.add(d.id);
            });
            await Promise.allSettled(Array.from(toDeleteFw).map(id => deleteDoc(doc(db, 'active_frameworks', id))));
        } catch (e) {
            console.warn('[Decommission] Step 1 (active_frameworks) non-fatal:', e);
        }
        updateStep(1, 'done');

        // Step 2 — Saved intel library copies (non-fatal)
        updateStep(2, 'running');
        try {
            const libSnap = await getDocs(query(collection(db, 'saved_intel'), where('mappedTo', '==', project.id)));
            await Promise.allSettled(libSnap.docs.map(d => deleteDoc(doc(db, 'saved_intel', d.id))));
        } catch (e) {
            console.warn('[Decommission] Step 2 (saved_intel) non-fatal:', e);
        }
        updateStep(2, 'done');

        // Step 3 — Plan reviews (non-fatal)
        updateStep(3, 'running');
        try {
            const reviewSnap = await getDocs(query(collection(db, 'plan_reviews'), where('projectId', '==', project.id)));
            await Promise.allSettled(reviewSnap.docs.map(d => deleteDoc(doc(db, 'plan_reviews', d.id))));
        } catch (e) {
            console.warn('[Decommission] Step 3 (plan_reviews) non-fatal:', e);
        }
        updateStep(3, 'done');

        // Step 4 — Generated reports (non-fatal)
        updateStep(4, 'running');
        try {
            const reportsSnap = await getDocs(query(collection(db, 'generated_reports'), where('projectId', '==', project.id)));
            await Promise.allSettled(reportsSnap.docs.map(d => deleteDoc(doc(db, 'generated_reports', d.id))));
        } catch (e) {
            console.warn('[Decommission] Step 4 (generated_reports) non-fatal:', e);
        }
        updateStep(4, 'done');

        // Step 5 — Delete the project itself (FATAL)
        try {
            updateStep(5, 'running');
            await deleteDoc(doc(db, 'srm_projects', project.id));
            updateStep(5, 'done');
        } catch (err: any) {
            console.error('[Decommission] Step 5 FATAL (srm_projects delete):', err);
            setExecError(`Project deletion failed: ${err?.message}`);
            updateStep(5, 'error');
            return;
        }

        await new Promise(r => setTimeout(r, 800));
        onComplete();
    };

    if (!isOpen) return null;

    const isConfirmValid = confirmText.trim() === projectName.trim();

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
            <div className={`absolute inset-0 bg-slate-950/80 backdrop-blur-sm ${step < 4 ? 'cursor-pointer' : ''}`} onClick={step < 4 ? onClose : undefined} />

            <div className="relative bg-white rounded-3xl shadow-2xl border border-slate-200 max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-200">

                {/* ── Header ── */}
                <div className="bg-gradient-to-r from-red-700 to-red-600 px-6 py-5 text-white flex items-start justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <ShieldExclamationIcon className="w-5 h-5 text-red-200" />
                            <span className="text-xs font-black uppercase tracking-[0.2em] text-red-200">
                                Framework Decommission
                            </span>
                        </div>
                        <h2 className="text-xl font-black leading-tight">{projectName}</h2>
                        <p className="text-red-100 text-sm mt-1">
                            Step {step} of {step < 4 ? '3' : '3'} — {
                                step === 1 ? 'Impact Review' :
                                step === 2 ? 'Decommission Reason' :
                                step === 3 ? 'Final Confirmation' :
                                'Executing'
                            }
                        </p>
                    </div>
                    {step < 4 && (
                        <button onClick={onClose} className="text-red-200 hover:text-white transition-colors mt-1">
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* ── Step progress bar ── */}
                <div className="flex h-1">
                    {[1, 2, 3].map(n => (
                        <div
                            key={n}
                            className={`flex-1 transition-colors duration-500 ${
                                step > n ? 'bg-red-600' :
                                step === n ? 'bg-red-400' :
                                'bg-slate-100'
                            }`}
                        />
                    ))}
                </div>

                <div className="p-6">

                    {/* ══ STEP 1 — Impact Review ══ */}
                    {step === 1 && (
                        <div className="space-y-4">
                            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex gap-3">
                                <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-bold text-red-800">This action is permanent and irreversible.</p>
                                    <p className="text-xs text-red-700 mt-0.5">All associated data will be permanently deleted after an audit record is written.</p>
                                </div>
                            </div>

                            <div>
                                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Records that will be permanently removed</p>
                                {loadingImpact ? (
                                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                                        <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                                        Calculating impact...
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {[
                                            { label: 'Framework modules / components', count: impact?.moduleCount ?? 0, icon: DocumentTextIcon },
                                            { label: 'Active framework documents', count: impact?.frameworkDocs ?? 0, icon: ShieldExclamationIcon },
                                            { label: 'Risk register entries', count: impact?.riskCount ?? 0, icon: ExclamationTriangleIcon },
                                            { label: 'Linked intelligence reports', count: impact?.linkedReports ?? 0, icon: DocumentTextIcon },
                                            { label: 'Library vault copies', count: impact?.linkedLibraryItems ?? 0, icon: DocumentTextIcon },
                                            { label: 'Plan review records', count: impact?.linkedReviews ?? 0, icon: ClockIcon },
                                        ].map(({ label, count, icon: Icon }) => (
                                            <div key={label} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                                                <div className="flex items-center gap-2">
                                                    <Icon className="w-4 h-4 text-slate-400" />
                                                    <span className="text-sm text-slate-700">{label}</span>
                                                </div>
                                                <span className={`text-sm font-black ${count > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                                                    {count}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                                <p className="text-xs text-emerald-800 font-medium">
                                    An immutable decommission audit record will be written to the <strong>decommission log</strong> before any data is deleted, providing a permanent paper trail.
                                </p>
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors">
                                    Cancel
                                </button>
                                <button
                                    onClick={() => setStep(2)}
                                    disabled={loadingImpact}
                                    className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-black text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    Continue <ArrowRightIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ══ STEP 2 — Reason ══ */}
                    {step === 2 && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                                    Reason for decommission <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                                >
                                    <option value="">Select a reason...</option>
                                    {DECOMMISSION_REASONS.map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                                    Additional notes (optional)
                                </label>
                                <textarea
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    rows={3}
                                    placeholder="Provide any additional context for the audit record..."
                                    className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm text-slate-800 focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                                />
                            </div>

                            <p className="text-xs text-slate-500">
                                This reason will be recorded in the decommission audit log alongside the operator's identity and timestamp.
                            </p>

                            <div className="flex gap-3 pt-2">
                                <button onClick={() => setStep(1)} className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors">
                                    Back
                                </button>
                                <button
                                    onClick={() => setStep(3)}
                                    disabled={!reason}
                                    className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-black text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Continue <ArrowRightIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ══ STEP 3 — Typed Confirmation ══ */}
                    {step === 3 && (
                        <div className="space-y-4">
                            <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl">
                                <p className="text-sm font-black text-red-800 mb-1">Final confirmation required</p>
                                <p className="text-xs text-red-700">
                                    Type the exact framework name below to unlock the decommission button. This cannot be undone.
                                </p>
                            </div>

                            <div className="p-3 bg-slate-900 rounded-lg font-mono text-sm text-amber-400 border border-slate-700 select-all text-center tracking-wide">
                                {projectName}
                            </div>

                            <div>
                                <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                                    Type framework name to confirm
                                </label>
                                <input
                                    type="text"
                                    value={confirmText}
                                    onChange={e => setConfirmText(e.target.value)}
                                    placeholder="Type the framework name exactly..."
                                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:ring-2 transition-colors ${
                                        confirmText && !isConfirmValid
                                            ? 'border-red-300 focus:ring-red-400 text-red-700'
                                            : isConfirmValid
                                            ? 'border-emerald-400 focus:ring-emerald-400 text-emerald-800'
                                            : 'border-slate-300 focus:ring-red-500'
                                    }`}
                                    autoComplete="off"
                                />
                                {isConfirmValid && (
                                    <p className="text-xs text-emerald-600 font-bold mt-1 flex items-center gap-1">
                                        <CheckCircleIcon className="w-3.5 h-3.5" /> Confirmed
                                    </p>
                                )}
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button onClick={() => setStep(2)} className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors">
                                    Back
                                </button>
                                <button
                                    onClick={executeDecommission}
                                    disabled={!isConfirmValid}
                                    className="flex-1 py-3 rounded-xl bg-red-700 hover:bg-red-800 text-white font-black text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-red-900/20"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                    Decommission Framework
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ══ STEP 4 — Executing ══ */}
                    {step === 4 && (
                        <div className="space-y-4">
                            <div className="space-y-3">
                                {execSteps.map((s, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center">
                                            {s.status === 'done' && <CheckCircleIcon className="w-5 h-5 text-emerald-500" />}
                                            {s.status === 'running' && <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />}
                                            {s.status === 'pending' && <div className="w-3 h-3 rounded-full bg-slate-200" />}
                                            {s.status === 'error' && <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />}
                                        </div>
                                        <span className={`text-sm ${
                                            s.status === 'done' ? 'text-emerald-700 font-medium' :
                                            s.status === 'running' ? 'text-slate-900 font-bold' :
                                            s.status === 'error' ? 'text-red-600 font-bold' :
                                            'text-slate-400'
                                        }`}>
                                            {s.label}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {execError && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-xs font-bold text-red-800 mb-1">Decommission encountered an error</p>
                                    <p className="text-xs text-red-700">{execError}</p>
                                    {decommissionLogId && (
                                        <p className="text-xs text-red-600 mt-2">
                                            Audit log written — ID: <code className="font-mono">{decommissionLogId}</code>
                                        </p>
                                    )}
                                    <button
                                        onClick={onClose}
                                        className="mt-3 w-full py-2 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-colors"
                                    >
                                        Close and investigate
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default FrameworkDecommissionModal;
